/**
 * Encryption and decryption utilities for quiz data
 * Compatible with the Python implementation in test.py
 *
 * Requires crypto-js: npm install crypto-js
 */

// Import crypto-js
import CryptoJS from "crypto-js";

/**
 * Generate an encryption key from userPhoneNumber and quizId
 * The key format is `${userPhoneNumber}__${quizId}` padded to 32 bytes
 *
 * @param {string} userPhoneNumber - The user's phone number
 * @param {string} quizId - The quiz ID
 * @returns {WordArray} A 32-byte key for AES-256 encryption
 */
function generateKey(userPhoneNumber, quizId) {
  const keyString = `${userPhoneNumber.split("").reverse().join("")}__${quizId
    .split("")
    .reverse()
    .join("")}`;
  // Ensure the key is 32 bytes long (padded if necessary)
  return CryptoJS.enc.Utf8.parse(keyString.padEnd(32, " "));
}

/**
 * Decrypt text that was encrypted with encryptText()
 *
 * @param {string} userPhoneNumber - The user's phone number (same as used for encryption)
 * @param {string} quizId - The quiz ID (same as used for encryption)
 * @param {string} encryptedText - Base64-encoded encrypted data
 * @returns {string} The decrypted text
 */
function decryptText(userPhoneNumber, quizId, encryptedText) {
  // Generate the same key used for encryption
  const key = generateKey(userPhoneNumber, quizId);

  // Decode the base64 string
  const encryptedData = CryptoJS.enc.Base64.parse(encryptedText);

  // Extract the IV (first 16 bytes)
  const iv = CryptoJS.lib.WordArray.create(encryptedData.words.slice(0, 4), 16);

  // Extract the ciphertext (everything after the IV)
  const ciphertext = CryptoJS.lib.WordArray.create(
    encryptedData.words.slice(4),
    encryptedData.sigBytes - 16
  );

  // Create a CipherParams object with the extracted components
  const cipherParams = CryptoJS.lib.CipherParams.create({
    ciphertext: ciphertext,
  });

  // Decrypt the data
  const decrypted = CryptoJS.AES.decrypt(cipherParams, key, {
    iv: iv,
    padding: CryptoJS.pad.Pkcs7,
    mode: CryptoJS.mode.CBC,
  });

  // Convert to UTF-8 string
  return decrypted.toString(CryptoJS.enc.Utf8);
}

export { decryptText };
